import React, { useState, useRef, useCallback, useEffect } from 'react';
import ScheduleSendMenu from '../ScheduleSendMenu';
import CheckSendMenu from '../CheckSendMenu';
import { fmtBkk } from '../../utils/bkkTime';
import EmailTagInput from '../EmailTagInput';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TEXT_COLORS, IndentParagraph, shiftIndent } from '../RichTextEditor';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { ComposeRestore, Email } from '../../types';
import { makePasteHandlers, genId, wrapQuote, splitQuote, buildOutgoingBody } from '../../utils/compose';
import { sendWarnings, SendWarning } from '../../utils/sendChecks';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';
import { useAutoCc } from '../../hooks/useAutoCc';
import AIReplyModal from '../Modals/AIReplyModal';
import SendCheckModal from '../Modals/SendCheckModal';
import AttachFileMenu from '../AttachFileMenu';

// Convert AI plain-text output to simple HTML paragraphs
function textToHtml(text: string): string {
  const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return String(text || '').split(/\n{2,}/).map(p => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
}


// ── Lang options for target language picker ──────────────────────────────
const LANG_OPTIONS = [
  { code: 'en', label: 'English' }, { code: 'zh', label: '中文' },
  { code: 'th', label: 'Thai' }, { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' }, { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' }, { code: 'es', label: 'Spanish' },
  { code: 'pt', label: 'Portuguese' }, { code: 'ar', label: 'Arabic' },
];

// Use the shared autocomplete-enabled tag input
const TagInput = EmailTagInput;

// `h`: 20 inside the Link · Image · paperclip bubble, so it stands as tall as the AI pill beside it.
function TBtn({ onClick, active, title, icon, h = 26 }: { onClick: () => void; active?: boolean; title: string; icon: string; h?: number }) {
  return (
    <button onMouseDown={e => { e.preventDefault(); onClick(); }} title={title} aria-label={title}
      style={{ width: 26, height: h, borderRadius: 'var(--radius-sm)', border: 'none', background: active ? 'var(--c-gray-100)' : 'transparent', color: active ? 'var(--text-primary)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden />
    </button>
  );
}

function HtmlPreview({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(80);
  const onLoad = useCallback(() => {
    const doc = ref.current?.contentDocument || ref.current?.contentWindow?.document;
    if (doc) setHeight(Math.max(60, (doc.documentElement.scrollHeight || doc.body.scrollHeight) + 10));
  }, []);
  return <iframe ref={ref} srcDoc={html} onLoad={onLoad} sandbox="allow-same-origin" title="preview"
    style={{ border: 'none', width: '100%', height, display: 'block', background: 'transparent' }} />;
}

function PreviewModal({ toList, ccList, subject, html, attachments, targetLangLabel, onSend, onClose, sending }:
  { toList: string[]; ccList: string[]; subject: string; html: string; attachments: { file: File; id: string }[]; targetLangLabel: string; onSend: () => void; onClose: () => void; sending: boolean }) {
  const [iframeH, setIframeH] = useState(300);
  const ref = useRef<HTMLIFrameElement>(null);
  const onLoad = useCallback(() => {
    const doc = ref.current?.contentDocument || ref.current?.contentWindow?.document;
    if (doc) setIframeH(Math.max(200, (doc.documentElement.scrollHeight || doc.body.scrollHeight) + 20));
  }, []);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="ti ti-eye" style={{ fontSize: 16, color: 'var(--accent)' }} aria-hidden />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Preview Email</span>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--accent-bg)', color: 'var(--accent-text)', fontWeight: 500 }}>
              <i className="ti ti-language" style={{ fontSize: 10, marginRight: 3 }} aria-hidden />{targetLangLabel}
            </span>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>
        <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', background: 'var(--c-gray-50)', flexShrink: 0 }}>
          <div style={{ fontSize: 12, marginBottom: 3 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 600, marginRight: 6 }}>To:</span>{toList.join(', ')}</div>
          {ccList.length > 0 && <div style={{ fontSize: 12, marginBottom: 3 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 600, marginRight: 6 }}>CC:</span>{ccList.join(', ')}</div>}
          <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 600, marginRight: 6 }}>Subject:</span><b>{subject}</b></div>
          {attachments.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {attachments.map(a => <span key={a.id} style={{ fontSize: 11, padding: '2px 8px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}><i className="ti ti-paperclip" style={{ fontSize: 10, marginRight: 3 }} aria-hidden />{a.file.name}</span>)}
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <iframe ref={ref} srcDoc={html} onLoad={onLoad} sandbox="allow-same-origin" title="preview"
            style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', width: '100%', height: iframeH, display: 'block' }} />
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button onClick={onSend} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
            <i className="ti ti-send" style={{ fontSize: 13 }} aria-hidden />{sending ? 'Sending…' : 'Confirm & Send'}
          </button>
          <button onClick={onClose} disabled={sending} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Back to edit</button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <i className="ti ti-wand" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />Translated version will be sent
          </span>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: '24px 28px', width: 340, textAlign: 'center' }}>
        <i className="ti ti-alert-triangle" style={{ fontSize: 32, color: '#E24B4A', marginBottom: 12, display: 'block' }} aria-hidden />
        <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 20, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onCancel} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Discard</button>
        </div>
      </div>
    </div>
  );
}

// `restore`: Undo send opened this composer again (components/UndoSend) — its fields start as they were when Send
// was pressed; `draft` is then the draft it was first opened from, if any, which still gives the quote.
interface Props { onClose: () => void; draft?: Email; restore?: ComposeRestore }

export default function ComposeNew({ onClose, draft, restore }: Props) {
  const { t } = useTranslation();
  const { uiLang, accounts, activeAccountId, showToast, incComposing, decComposing, loadEmails } = useStore();

  // Pause background refresh while this composer is open (protects current work). The user's Undo send
  // choice comes with the Email layout; loadUndoSend asks for it if this page hasn't had it.
  useEffect(() => {
    incComposing();
    useStore.getState().loadUndoSend();
    return () => decComposing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill from draft if provided. A draft body may contain a quoted/forwarded
  // original (wrapped sentinel) — split it so the quote stays read-only & intact.
  const draftSplit = draft ? splitQuote(draft.bodyOriginal || '') : { userHtml: '', quoteHtml: '' };
  const [quoteHtml] = useState<string>(draftSplit.quoteHtml);

  const draftAccount = draft ? accounts.find(a => a.email === draft.from.email) : null;
  // Where the fields start: the email as Undo send gave it back, else the draft, else blank.
  const init: ComposeRestore = restore || {
    draftId: draft?.id || null,
    accountId: draftAccount?.id || activeAccountId || accounts[0]?.id || '',
    to: draft?.to || [], cc: draft?.cc || [], bcc: draft?.bcc || [],
    subject: draft?.subject === '(No subject)' ? '' : (draft?.subject || ''),
    html: draftSplit.userHtml || '', preview: '',
    composedLang: uiLang, targetLang: draft?.lang || uiLang, files: [],
  };
  const [fromAccountId, setFromAccountId] = useState(init.accountId);
  const [toList, setToList] = useState<string[]>(init.to);
  const [ccTyped, setCcTyped] = useState<string[]>(init.cc);
  const [bccTyped, setBccTyped] = useState<string[]>(init.bcc);
  const [subject, setSubject] = useState(init.subject);
  const [showCc, setShowCc] = useState(init.cc.length > 0);
  const [showBcc, setShowBcc] = useState(init.bcc.length > 0);
  // The user's Auto CC / BCC for the From mailbox join as ordinary chips after the typed ones (see
  // hooks/useAutoCc) — changing From swaps them — and open their row. A draft that already carries
  // CC or BCC is left as it is, and so is an email Undo send gave back (its chips are already in, and
  // one the user removed stays removed). ccList / bccList are what is shown, previewed, saved and sent.
  const { autoCc, autoBcc, dropAuto } = useAutoCc({
    enabled: !restore && !init.cc.length && !init.bcc.length,
    fromAccountId,
    fromEmail: accounts.find(a => a.id === fromAccountId)?.email || '',
    to: toList, cc: ccTyped, bcc: bccTyped,
  });
  const ccList = [...ccTyped, ...autoCc];
  const bccList = [...bccTyped, ...autoBcc];
  useEffect(() => { if (autoCc.length) setShowCc(true); }, [autoCc.length]);
  useEffect(() => { if (autoBcc.length) setShowBcc(true); }, [autoBcc.length]);
  const [targetLang, setTargetLang] = useState<string>(init.targetLang);
  // Language the editor content is written in (switches to customer's language when AI fills it)
  const [composedLang, setComposedLang] = useState<string>(init.composedLang);
  const [showAI, setShowAI] = useState(false);
  const [attachments, setAttachments] = useState<{ file: File; id: string }[]>(() => init.files.map(file => ({ file, id: genId() })));
  // A file picked from Our Documents or the Drop Folder is still on its way (AttachFileMenu): Send waits for it.
  const [attaching, setAttaching] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Pre-send reminder on screen (blank subject / "attached" but nothing attached) and what
  // "Send anyway" carries on with: opening the preview, or the send itself.
  const [sendCheck, setSendCheck] = useState<{ warnings: SendWarning[]; next: 'preview' | 'send' | 'schedule' | 'check'; at?: number; reviewer?: string } | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  const [translationPreview, setTranslationPreview] = useState(init.preview);
  const [translating, setTranslating] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Signature — stored separately to preserve inline styles (TipTap strips them)
  const [signature, setSignature] = useState<string>('');

  const fileRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLInputElement>(null);

  const needsTranslation = targetLang !== composedLang;
  // useEditor's onUpdate closure is created once — read live language state through a ref.
  const transRef = useRef<{ needs: boolean; from: string; to: string }>({ needs: false, from: uiLang, to: uiLang });
  transRef.current = { needs: needsTranslation, from: composedLang, to: targetLang };

  // Paste / drop handlers: images go inline, other files become attachments.
  const pasteHandlers = makePasteHandlers((file) =>
    setAttachments(prev => [...prev, { file, id: genId() }])
  );

  const editor = useEditor({
    // Colour and indent go out as inline styles, which the recipient's mail program shows. IndentParagraph
    // takes the place of StarterKit's own paragraph (two extensions of one name make TipTap warn).
    extensions: [StarterKit.configure({ paragraph: false }), IndentParagraph, Underline, TextStyle, Color,
      Link.configure({ openOnClick: false }), Image, Placeholder.configure({ placeholder: 'Write your message…' })],
    content: init.html,
    editorProps: { handlePaste: pasteHandlers.handlePaste, handleDrop: pasteHandlers.handleDrop },
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      const html = editor.getHTML();
      if (!text.trim()) { setTranslationPreview(''); return; }
      if (!transRef.current.needs) { setTranslationPreview(html); return; }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setTranslating(true);
        try {
          const { translated } = await api.translateText(html, transRef.current.from, transRef.current.to);
          setTranslationPreview(translated);
        } catch {}
        setTranslating(false);
      }, 3000);
    },
  });

  // Re-translate when target language changes
  useEffect(() => {
    if (!needsTranslation) { setTranslationPreview(editor?.getHTML() || ''); return; }
    const text = editor?.getText();
    if (!text?.trim()) { setTranslationPreview(''); return; }
    setTranslating(true);
    api.translateText(editor!.getHTML(), composedLang, targetLang)
      .then(r => setTranslationPreview(r.translated))
      .catch(() => {})
      .finally(() => setTranslating(false));
  }, [targetLang]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Load signature separately (don't put in TipTap — it strips inline styles)
  useEffect(() => {
    if (!fromAccountId) return;
    api.getSignature(fromAccountId)
      .then(({ signature: sig }) => setSignature(sig || ''))
      .catch(() => {});
  }, [fromAccountId]);

  // ── Auto-save draft (offline-safe, survives crashes) ──
  const { clearDraft, saveNow, draftIdRef } = useAutoSaveDraft({
    active: !sending,
    initialDraftId: init.draftId,
    getSnapshot: () => ({
      accountId: fromAccountId,
      to: toList, cc: ccList, bcc: bccList,
      subject: subject || '(No subject)',
      bodyHtml: (editor?.getHTML() || '') + wrapQuote(quoteHtml),
      bodyLang: composedLang,
      targetLang,
    }),
  });

  const handleImg = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const reader = new FileReader();
    reader.onload = ev => editor.chain().focus().setImage({ src: ev.target?.result as string }).run();
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    setAttachments(prev => [...prev, ...files.map(f => ({ file: f, id: Math.random().toString(36).slice(2) }))]);
    e.target.value = '';
  };

  // True when a pre-send reminder is now showing: the caller stops, and "Send anyway" resumes `next`.
  const remindBeforeSend = (next: 'preview' | 'send' | 'schedule' | 'check', at?: number, reviewer?: string) => {
    const warnings = sendWarnings(subject, editor?.getHTML() || '', attachments.length);
    if (warnings.length) setSendCheck({ warnings, next, at, reviewer });
    return warnings.length > 0;
  };
  // Schedule send (Bugify "Schedule Send Emails", Calem P0): the same checks and payload as Send
  // Now, parked on the server until `at` (ms). Own personal mailbox only — the ▾ is shown only then.
  const doSchedule = async (at: number, checked = false) => {
    if (inFlight.current) return;
    if (attaching) { showToast(t('eml_attach_wait')); return; }
    if (![...toList, ...ccList, ...bccList].some(a => String(a || '').trim())) {
      try { await saveNow(); } catch { /* keep going — the toast still tells them */ }
      showToast('Add a recipient before scheduling — your draft has been saved');
      return;
    }
    const html = editor?.getHTML() || '';
    if (!html.replace(/<[^>]+>/g, '').trim() && attachments.length === 0) { showToast('Please write a message before scheduling'); return; }
    if (toList.length === 0) { showToast('Please add at least one recipient'); return; }
    if (!fromAccountId) { showToast('Please select a sender account'); return; }
    if (!checked && remindBeforeSend('schedule', at)) return;
    const payload = {
      accountId: fromAccountId,
      to: toList, cc: ccList, bcc: bccList,
      subject: subject || '(No subject)',
      bodyHtml: buildOutgoingBody(html, signature, quoteHtml),
      bodyLang: composedLang,
      targetLang: needsTranslation ? targetLang : composedLang,
      files: attachments.map(a => a.file),
      sendAt: Math.floor(at / 1000),
    };
    const subjSnap = subject || '(No subject)';
    inFlight.current = true;
    try { await saveNow(); } catch { /* draft is a best-effort safety net */ }
    showToast('Scheduling…');
    onClose();
    api.scheduleEmail(payload).then(async () => {
      try { await clearDraft(); } catch {}
      const st = useStore.getState();
      st.showToast(t('eml_sched_done', { time: fmtBkk(at, uiLang) }));
      st.loadEmails({ silent: true });
    }).catch((e: any) => {
      useStore.getState().setBlockingError(
        `"${subjSnap}"\n\n${e?.message || e}\n\nYour message is saved in Drafts — open it there to send again.`
      );
    });
  };
  const canSchedule = !!accounts.find(a => a.id === fromAccountId)?.personal;
  // Check (report 4aaa018b): the same checks and payload as Send Now, parked on the server until a supervisor decides. From any
  // mailbox this login can open. The composer closes as Send does.
  const doCheck = async (reviewer: string, checked = false) => {
    if (inFlight.current) return;
    if (attaching) { showToast(t('eml_attach_wait')); return; }
    if (![...toList, ...ccList, ...bccList].some(a => String(a || '').trim())) {
      try { await saveNow(); } catch { /* keep going — the toast still tells them */ }
      showToast(t('eml_check_add_recipient'));
      return;
    }
    const html = editor?.getHTML() || '';
    if (!html.replace(/<[^>]+>/g, '').trim() && attachments.length === 0) { showToast(t('eml_check_write_first')); return; }
    if (toList.length === 0) { showToast('Please add at least one recipient'); return; }
    if (!fromAccountId) { showToast('Please select a sender account'); return; }
    if (!checked && remindBeforeSend('check', undefined, reviewer)) return;
    const payload = {
      accountId: fromAccountId,
      to: toList, cc: ccList, bcc: bccList,
      subject: subject || '(No subject)',
      bodyHtml: buildOutgoingBody(html, signature, quoteHtml),
      bodyLang: composedLang,
      targetLang: needsTranslation ? targetLang : composedLang,
      files: attachments.map(a => a.file),
      reviewer,
    };
    const subjSnap = subject || '(No subject)';
    inFlight.current = true;
    try { await saveNow(); } catch { /* draft is a best-effort safety net */ }
    showToast(t('eml_check_asking'));
    onClose();
    api.checkEmail(payload).then(async r => {
      try { await clearDraft(); } catch {}
      const st = useStore.getState();
      st.showToast(r.reviewerName ? t('eml_check_done', { name: r.reviewerName }) : t('eml_check_done_any'));
      st.loadEmails({ silent: true });
    }).catch((e: any) => {
      useStore.getState().setBlockingError(
        `"${subjSnap}"\n\n${e?.message || e}\n\nYour message is saved in Drafts — open it there to send again.`
      );
    });
  };

  // `checked` = the pre-send reminders were already passed (Send anyway, or the preview's Confirm).
  // One submission per composer: a second click during the pre-close draft save used to post twice.
  const inFlight = useRef(false);
  const doSend = async (checked = false) => {
    if (inFlight.current) return;
    if (attaching) { showToast(t('eml_attach_wait')); return; }
    // Never post an addressless send: it cannot succeed, and the failure used to lose the message
    // because the draft is only written to the server AFTER the send (owner, 2026-08-22).
    if (![...toList, ...ccList, ...bccList].some(a => String(a || '').trim())) {
      try { await saveNow(); } catch { /* keep going — the toast still tells them */ }
      showToast('Add a recipient before sending — your draft has been saved');
      return;
    }
    const html = editor?.getHTML() || '';
    if (!html.replace(/<[^>]+>/g, '').trim() && attachments.length === 0) {
      showToast('Please write a message before sending'); return;
    }
    if (toList.length === 0) { showToast('Please add at least one recipient'); return; }
    if (!fromAccountId) { showToast('Please select a sender account'); return; }
    if (!checked && remindBeforeSend('send')) return;
    // Async send: close immediately, deliver behind the scenes — after the user's Undo send seconds,
    // if any (store.queueSend); a failure raises a BLOCKING dialog and the auto-saved draft stays in
    // Drafts for retry. The body goes out under the editor's typography (utils/compose), which is
    // also what the preview shows.
    const payload = {
      accountId: fromAccountId,
      to: toList, cc: ccList, bcc: bccList,
      subject: subject || '(No subject)',
      bodyHtml: buildOutgoingBody(html, signature, quoteHtml),
      bodyLang: composedLang,
      targetLang: needsTranslation ? targetLang : composedLang,
      files: attachments.map(a => a.file),
    };
    const subjSnap = subject || '(No subject)';
    inFlight.current = true;
    try { await saveNow(); } catch { /* draft is a best-effort safety net */ }
    onClose();
    useStore.getState().queueSend({
      subject: subjSnap, payload, clearDraft, draftId: () => draftIdRef.current,
      // What Undo opens again: this composer as it is now, attachments included.
      restore: { accountId: fromAccountId, to: toList, cc: ccList, bcc: bccList, subject, html, preview: translationPreview, composedLang, targetLang, files: payload.files },
      reopen: { kind: 'new', draft },
    });
  };

  const handleSaveDraft = async () => {
    if (savingDraft) return;
    const html = editor?.getHTML() || '';
    if (!html.replace(/<[^>]+>/g, '').trim() && !subject && toList.length === 0) {
      showToast('Nothing to save'); return;
    }
    setSavingDraft(true);
    try {
      await saveNow();
      showToast('Draft saved');
      loadEmails();
      onClose();
    } catch (e: any) {
      showToast('Failed to save draft: ' + e.message);
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePreviewSend = () => {
    if (!editor?.getText().trim() && attachments.length === 0) { showToast('Please write a message first'); return; }
    if (toList.length === 0) { showToast('Please add a recipient'); return; }
    if (remindBeforeSend('preview')) return;
    setShowPreview(true);
  };

  const fmtBytes = (b: number) => b < 1024 ? b + 'B' : b < 1048576 ? (b / 1024).toFixed(0) + 'KB' : (b / 1048576).toFixed(1) + 'MB';
  const targetLangLabel = LANG_OPTIONS.find(l => l.code === targetLang)?.label || targetLang;

  const inputStyle = { flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: 'var(--text-primary)', padding: '5px 0' };

  return (
    <>
      {/* The three mf-compose classes do nothing here: they are what the phone's Email app (MailApp/EmailApp.tsx, report
          0e8dd0cb) lays this same composer out full-screen by, while that app is the page. */}
      <div className="mf-compose" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: '2px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className={`ti ${draft ? 'ti-file-pencil' : 'ti-pencil'}`} style={{ fontSize: 15, color: 'var(--accent)' }} aria-hidden />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {draft ? 'Edit Draft' : 'New Email'}
              </span>
              {draft && <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#FFF3CD', color: '#7B5200', border: '1px solid #F5C842', fontWeight: 600 }}>DRAFT</span>}
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <i className="ti ti-cloud-check" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />auto-saved
              </span>
            </div>
            {/* Close keeps the draft (it's auto-saved). Use Discard to delete it. */}
            <button onClick={() => { loadEmails(); onClose(); }} title="Close (keeps draft)" style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
              <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* Fields */}
            <div style={{ padding: '0 16px', borderBottom: '1px solid var(--border)' }}>
              {/* From */}
              <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', minHeight: 36 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', width: 36, flexShrink: 0, textAlign: 'right', marginRight: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</span>
                <select value={fromAccountId} onChange={e => setFromAccountId(e.target.value)}
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: 'var(--text-primary)', padding: '5px 0', cursor: 'pointer' }}>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name} &lt;{a.email}&gt;</option>)}
                </select>
              </div>

              {/* To */}
              <TagInput label="To" values={toList} onAdd={v => setToList(p => [...p, v])} onRemove={v => setToList(p => p.filter(x => x !== v))} />

              {showCc && <TagInput label="CC" values={ccList} onAdd={v => setCcTyped(p => [...p, v])} onRemove={v => { if (autoCc.includes(v)) dropAuto(v); else setCcTyped(p => p.filter(x => x !== v)); }} />}
              {showBcc && <TagInput label="BCC" values={bccList} onAdd={v => setBccTyped(p => [...p, v])} onRemove={v => { if (autoBcc.includes(v)) dropAuto(v); else setBccTyped(p => p.filter(x => x !== v)); }} />}
              {/* Each + stays until its own row is open — an auto-opened CC row must not hide + BCC */}
              {(!showCc || !showBcc) && (
                <div style={{ display: 'flex', gap: 8, padding: '3px 0 3px 46px' }}>
                  {!showCc && <button onClick={() => setShowCc(true)} style={{ border: 'none', background: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>+ CC</button>}
                  {!showBcc && <button onClick={() => setShowBcc(true)} style={{ border: 'none', background: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>+ BCC</button>}
                </div>
              )}

              {/* Subject */}
              <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', minHeight: 36 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', width: 36, flexShrink: 0, textAlign: 'right', marginRight: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subj</span>
                <input ref={subjectRef} value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" style={inputStyle} />
              </div>

              {/* Target language */}
              <div style={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', width: 36, flexShrink: 0, textAlign: 'right', marginRight: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Send as</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select value={targetLang} onChange={e => setTargetLang(e.target.value)}
                    style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--c-gray-50)', fontSize: 12, color: 'var(--text-primary)', padding: '4px 8px', cursor: 'pointer', outline: 'none' }}>
                    {LANG_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                  {needsTranslation && (
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                      Writing in <b style={{ color: 'var(--text-primary)' }}>{LANG_OPTIONS.find(l => l.code === composedLang)?.label || composedLang}</b>
                      {' → auto-translated to '}
                      <b style={{ color: 'var(--accent)' }}>{targetLangLabel}</b>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Pane 1: User's message */}
            <div style={{ borderBottom: needsTranslation ? '1px solid var(--border)' : 'none' }}>
              <div className="mf-compose-tools" style={{ padding: '4px 16px', display: 'flex', alignItems: 'center', gap: 4, background: 'var(--c-gray-50)', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your message</span>
                <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>· {LANG_OPTIONS.find(l => l.code === composedLang)?.label || composedLang}</span>
                <div style={{ flex: 1 }} />
                {/* Merged AI button — knowledge-grounded compose */}
                <button onMouseDown={e => { e.preventDefault(); setShowAI(true); }} title="AI — knowledge-grounded draft from your instruction"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--accent)', background: 'var(--accent-bg)', color: 'var(--accent)', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 4 }}>
                  <i className="ti ti-sparkles" style={{ fontSize: 12 }} aria-hidden />AI
                </button>
                {/* Link · Image · the paperclip in a bubble of their own beside AI (report 2454c57a, 2nd opening): AI's
                    outlined pill, in a neutral grey. The paperclip's pickers start from this email's job or recipient. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '0 2px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-200)', marginRight: 4 }}>
                  <TBtn h={20} onClick={() => { const url = window.prompt('Enter URL'); if (url && editor) editor.chain().focus().setLink({ href: url }).run(); }} active={editor?.isActive('link')} title="Link" icon="ti-link" />
                  <TBtn h={20} onClick={() => imgRef.current?.click()} title="Insert image" icon="ti-photo" />
                  {/* From this computer · Our Documents · the Drop Folder (report 2454c57a) */}
                  <AttachFileMenu title="Attach file" onComputer={() => fileRef.current?.click()}
                    onFile={file => setAttachments(prev => [...prev, { file, id: genId() }])} onBusy={setAttaching}
                    email={{ subject, to: toList }} />
                  <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={handleFiles} />
                  <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImg} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} title="Bold" icon="ti-bold" />
                  <TBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} title="Italic" icon="ti-italic" />
                  <TBtn onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} title="Underline" icon="ti-underline" />
                  <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
                  <TBtn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} title="Bullet list" icon="ti-list" />
                  <TBtn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')} title="Numbered list" icon="ti-list-numbers" />
                  <TBtn onClick={() => editor && shiftIndent(editor, -1)} title={t('eml_fmt_outdent')} icon="ti-indent-decrease" />
                  <TBtn onClick={() => editor && shiftIndent(editor, 1)} title={t('eml_fmt_indent')} icon="ti-indent-increase" />
                  <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
                  {TEXT_COLORS.map(c => (
                    <button key={c} type="button" title={t('eml_fmt_color', { color: c })} onMouseDown={e => { e.preventDefault(); editor?.chain().focus().setColor(c).run(); }}
                      style={{ width: 15, height: 15, borderRadius: '50%', background: c, border: '1px solid var(--border-strong)', cursor: 'pointer', padding: 0, margin: '0 1px' }} />
                  ))}
                  <TBtn onClick={() => editor?.chain().focus().unsetColor().run()} title={t('eml_fmt_no_color')} icon="ti-ban" />
                </div>
              </div>
              <div style={{ padding: '10px 16px', minHeight: 120 }}>
                <EditorContent editor={editor} />
              </div>
              {/* Signature — outside TipTap to keep inline styles intact */}
              {signature && (
                <div style={{ padding: '0 16px 10px', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="ti ti-signature" style={{ fontSize: 10 }} aria-hidden />Signature
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: signature }} style={{ fontSize: 13 }} />
                </div>
              )}
              {/* Quoted original (when editing a reply/forward draft) — read-only, full fidelity */}
              {quoteHtml && (
                <div style={{ padding: '0 16px 10px', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="ti ti-quote" style={{ fontSize: 10 }} aria-hidden />Quoted message
                  </div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                    <HtmlPreview html={quoteHtml} />
                  </div>
                </div>
              )}
              {attachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 16px 8px' }}>
                  {attachments.map(a => (
                    <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '3px 8px', fontSize: 11 }}>
                      <i className="ti ti-file" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />
                      <span style={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.file.name}</span>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>{fmtBytes(a.file.size)}</span>
                      <button onClick={() => setAttachments(p => p.filter(x => x.id !== a.id))} style={{ border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0, display: 'flex' }}>
                        <i className="ti ti-x" style={{ fontSize: 10 }} aria-hidden />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Pane 2: Translation preview */}
            {needsTranslation && (
              <div>
                <div style={{ padding: '4px 16px', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--accent-bg)', borderBottom: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                  <i className="ti ti-wand" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Auto-translated</span>
                  <span style={{ fontSize: 10, color: 'var(--accent-text)' }}>· {targetLangLabel} — this is what the recipient will receive</span>
                  {translating && <span style={{ fontSize: 10, color: 'var(--accent)', fontStyle: 'italic', marginLeft: 4 }}>Translating…</span>}
                </div>
                <div style={{ padding: '10px 16px', minHeight: 80, background: 'color-mix(in srgb, var(--accent) 3%, transparent)' }}>
                  {translationPreview
                    ? <HtmlPreview html={buildOutgoingBody(translationPreview)} />
                    : <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                        {editor?.getText().trim() ? 'Translation will appear after 3 seconds…' : 'Type your message above to see translation'}
                      </div>
                  }
                </div>
              </div>
            )}
          </div>

          {/* Action bar */}
          <div className="mf-compose-bar" style={{ padding: '10px 16px', borderTop: '2px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-surface)', flexShrink: 0, flexWrap: 'wrap' }}>
            <button onClick={handlePreviewSend} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <i className="ti ti-eye" style={{ fontSize: 13 }} aria-hidden />Preview &amp; Send
            </button>
            {/* Send Now — with the Send menu ▾ joined to it: Undo send for every sender, Schedule send
                when the sender is a personal mailbox */}
            <span style={{ display: 'flex', alignItems: 'stretch' }}>
              <button onClick={() => doSend()} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'transparent', color: 'var(--accent)', border: '1.5px solid var(--accent)', borderRadius: 'var(--radius-md) 0 0 var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
                <i className="ti ti-send" style={{ fontSize: 13 }} aria-hidden />{sending ? 'Sending…' : 'Send Now'}
              </button>
              <ScheduleSendMenu disabled={sending} uiLang={uiLang} canSchedule={canSchedule} onPick={at => doSchedule(at)} />
            </span>
            {/* Check — a supervisor reads it first (report 4aaa018b) */}
            <CheckSendMenu disabled={sending} onPick={reviewer => doCheck(reviewer)} />
            <button onClick={handleSaveDraft} disabled={savingDraft} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: savingDraft ? 'not-allowed' : 'pointer', opacity: savingDraft ? 0.7 : 1 }}>
              <i className="ti ti-device-floppy" style={{ fontSize: 13 }} aria-hidden />{savingDraft ? 'Saving…' : 'Save Draft'}
            </button>
            <button onClick={() => setShowDiscardConfirm(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'transparent', color: '#E24B4A', border: '1px solid rgba(226,75,74,0.3)', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}>
              <i className="ti ti-trash" style={{ fontSize: 13 }} aria-hidden />Discard
            </button>
            {needsTranslation && (
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)', width: '100%', display: 'flex', alignItems: 'center', gap: 3 }}>
                <i className="ti ti-wand" style={{ fontSize: 10, color: 'var(--accent)' }} aria-hidden />
                The {targetLangLabel} version will be sent to the recipient
              </span>
            )}
          </div>
        </div>
      </div>

      {showPreview && (
        <PreviewModal
          toList={toList} ccList={ccList} subject={subject || '(No subject)'}
          html={buildOutgoingBody(translationPreview || editor?.getHTML() || '')}
          attachments={attachments} targetLangLabel={targetLangLabel}
          onSend={() => doSend(true)} onClose={() => setShowPreview(false)} sending={sending}
        />
      )}

      {/* Pre-send reminder — Go back to fix the email, or Send anyway */}
      {sendCheck && (
        <SendCheckModal
          warnings={sendCheck.warnings}
          onBack={() => {
            if (sendCheck.warnings.some(w => w.kind === 'no_subject')) subjectRef.current?.focus();
            setSendCheck(null);
          }}
          onSendAnyway={() => {
            setSendCheck(null);
            if (sendCheck.next === 'preview') setShowPreview(true);
            else if (sendCheck.next === 'schedule' && sendCheck.at) doSchedule(sendCheck.at, true);
            else if (sendCheck.next === 'check' && sendCheck.reviewer) doCheck(sendCheck.reviewer, true);
            else doSend(true);
          }}
        />
      )}

      {showDiscardConfirm && (
        <ConfirmDialog
          message="Discard this email and delete the draft? This cannot be undone."
          onConfirm={async () => {
            setShowDiscardConfirm(false);
            await clearDraft();   // removes the auto-saved draft (server + local)
            loadEmails();
            onClose();
          }}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}

      {/* AI Reply assistant */}
      {showAI && (
        <AIReplyModal
          uiLang={uiLang}
          defaultTargetLang={targetLang}
          langOptions={LANG_OPTIONS}
          accountEmail={accounts.find(a => a.id === fromAccountId)?.email || ''}
          toEmail={toList[0] || ''}
          onClose={() => setShowAI(false)}
          onInsert={(text, lang) => {
            editor?.chain().focus().insertContent(textToHtml(text)).run();
            setComposedLang(lang);
            setTargetLang(lang);
            setTranslationPreview('');
            setShowAI(false);
          }}
        />
      )}
    </>
  );
}
